import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AIMode from '../components/AIMode/AIMode';
import DataMode from '../components/DataMode/DataMode';
import InitialsAvatar from '../components/Common/InitialsAvatar';
import { SupportModal } from '../components/Support/SupportModal';
import SettingsModal from '../components/Setting/SettingsModal';
import UserApi from '../api/platform/userApi';
import { removeCookie } from '../utils/cookiesUtils';
import {
  handleError,
  mapResponseErrorToSystemError,
  mapUnknownErrorToSystemError,
} from '../utils/errorUtils';
import { ResponseErrorModel } from '../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../models/system/responseSuccessModel';
import { UserModel } from '../models/platform/userModel';
import { useSystemError } from '../components/Error/SystemErrorContext';
import mainStyles from '../css/main.module.css';
import logo from '../assets/images/logo-transparent.png';
import MenuIcon from '@mui/icons-material/Menu';

const isMobile = () => window.innerWidth <= 768;

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<UserModel | null>(null);
  const [userEmail, setUserEmail] = useState<string>(location.state?.email || '');
  const [activeMode, setActiveMode] = useState<'search' | 'ai'>('ai');

  // Sidebar state lives here so header hamburger can control it
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile());

  const [showDropdown, setShowDropdown] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const { triggerSystemError } = useSystemError();
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Sync sidebar state on resize
  useEffect(() => {
    const handleResize = () => {
      if (isMobile()) setIsSidebarOpen(false);
      else setIsSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await UserApi.getUser();
        if (response.success) {
          const data = (response as ResponseSuccessModel).data;
          setUser(data.user);
          setUserEmail(data.user.email);
        } else {
          handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, () => {}, triggerSystemError);
        }
      } catch (err) {
        handleError(mapUnknownErrorToSystemError(err, false), null, () => {}, triggerSystemError);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    removeCookie('platform_token');
    navigate('/auth/login');
  };

  return (
    <div className={mainStyles.container}>
      {/* Header */}
      <header className={mainStyles.header}>
        {/* Hamburger — mobile only, left of logo */}
        <button
          className={mainStyles.hamburgerButton}
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-label="Toggle sidebar"
        >
          <MenuIcon />
        </button>

        <div className={mainStyles.logo}>
          <img src={logo} alt="Logo" className={mainStyles['logo-img']} />
        </div>

        <div ref={dropdownRef} className={mainStyles.userSection}>
          <div className={mainStyles.userInfo}>
            <span className={mainStyles.userEmail}>{userEmail}</span>
            <div
              className={mainStyles.avatarContainer}
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <InitialsAvatar name={userEmail} />
            </div>
          </div>
          {showDropdown && (
            <div className={mainStyles.dropdownMenu}>
              <div className={mainStyles.dropdownItem} onClick={() => { setIsSettingsModalOpen(true); setShowDropdown(false); }}>
                Settings
              </div>
              <div className={mainStyles.dropdownItem} onClick={() => { setIsHelpModalOpen(true); setShowDropdown(false); }}>
                Help
              </div>
              <div className={mainStyles.dropdownItem} onClick={handleLogout}>
                Logout
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Mode Tabs — hidden on mobile */}
      <div className={mainStyles.modeTabs}>
        <button
          className={`${mainStyles.modeTab} ${activeMode === 'ai' ? mainStyles.selectedTab : ''}`}
          onClick={() => setActiveMode('ai')}
        >
          Ask AI
        </button>
        <button
          className={`${mainStyles.modeTab} ${activeMode === 'search' ? mainStyles.selectedTab : ''}`}
          onClick={() => setActiveMode('search')}
        >
          Data Search
        </button>
      </div>

      {/* Mode Content */}
      <div className={mainStyles.modeContent} style={{ display: activeMode === 'ai' ? 'flex' : 'none' }}>
        <AIMode
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          onCloseSidebar={() => setIsSidebarOpen(false)}
        />
      </div>
      <div className={mainStyles.modeContent} style={{ display: activeMode === 'search' ? 'flex' : 'none' }}>
        <DataMode />
      </div>

      {/* Modals */}
      <SettingsModal open={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} />
      <SupportModal open={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} user={user} />
    </div>
  );
};

export default MainLayout;
